import { AnnouncementEnum } from "@gap/backend/app-services/announcement/enums/AnnouncementEnums"
import { AnnouncementDto } from "@gap/backend/app-services/announcement/models/AnnouncementDto"
import { apiClient } from "api/trpc/trpc-client"
import WebSocketStore from "websockets/WebSocketStore"
import { AnnouncementContent } from "./Announcements"
import AnnouncementXPMDisconnected from "./instances/AnnouncementXPMDisconnected"
import AnnouncementXPMSyncErrors from "./instances/AnnouncementXPMSyncErrors"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import AnnouncementGoose from "./instances/AnnouncementGoose"
import { reaction } from "mobx"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { AllAnnouncementsEnum } from "features/main/main-router/org-guard/main-container/announcement/Announcement"
import AnnouncementOffline from "./instances/AnnouncementOffline"
import userCache from "cache/userCache"
import { UserDto } from "@gap/backend/app-services/user/models/UserDto"

export default class AnnouncementStore {
   sp
   private announcements: AnnouncementDto[] = []

   constructor(sp: { webSocketStore: WebSocketStore, orgGuardStore: OrgGuardStore, userInfo: UserDto.info | undefined }) {
      this.sp = sp
      reaction(() => this.hasXpmConnectedAnnouncement, async () => {
         if (this.hasXpmConnectedAnnouncement) {
            // Eagerly set this in lieu of refetching the org
            this.sp.orgGuardStore.userOrg.xeroAuthorised = false
         }
      })
      sp.webSocketStore.connectionFailureCallbacks.push({
         onFailure: this.addOfflineAnnouncement,
         onReconnect: this.removeOfflineAnnouncement
      })
   }

   get allAnnouncements(): Record<AnnouncementEnum, AnnouncementContent> {
      return this.announcements.reduce((acc, a) => {
         acc[a.announcement] = {
            content: this.getAnnouncementContent(a.announcement),
            type: a.announcementType,
            dismissible: a.dismissible
         }
         return acc
      }, {} as Record<AnnouncementEnum, AnnouncementContent>)
   }

   get hasXpmConnectedAnnouncement() {
      return !!this.announcements.find(({ announcement }) => announcement === 'xpmDisconnected')
   }

   get shouldFetchAnnouncements() {
      const isServiceUser = userCache.serviceUser.get()
      return !isServiceUser
   }

   addGoose = () => {
      if (!this.announcements.find(x => x.announcement === 'goose')) {
         this.announcements.push({
            announcement: 'goose',
            announcementType: 'info',
            dismissible: false,
            orgId: '',
            userId: ''
         })

         // Remove goose after 3 seconds
         setTimeout(() => {
            this.announcements = this.announcements.filter(x => x.announcement !== 'goose')
         }, 3000)
      }
   }

   addOfflineAnnouncement = () => {
      if (!this.announcements.find(x => x.announcement === 'offline')) {
         this.announcements.push({
            announcement: 'offline',
            announcementType: 'error',
            dismissible: false,
            orgId: '',
            userId: ''
         })
      }
   }

   removeOfflineAnnouncement = () => {
      this.announcements = this.announcements.filter(x => x.announcement !== 'offline')
   }

   getAnnouncementContent = (announcement: AnnouncementEnum): React.ReactNode => {
      switch (announcement) {
         case 'xpmDisconnected':
            return <AnnouncementXPMDisconnected />
         case 'xpmSyncErrors':
            return <AnnouncementXPMSyncErrors />
         case 'goose':
            return <AnnouncementGoose />
         case 'offline':
            return <AnnouncementOffline />
         default:
            return null
      }
   }

   dismissAnnouncement = (announcementKey: AllAnnouncementsEnum) => () => {
      const announcementToDismiss = this.announcements.find(a => a.announcement === announcementKey)
      if (announcementToDismiss) {
         apiClient.user.dismissAnnouncement.mutate({ announcement: announcementToDismiss.announcement })
         this.announcements = this.announcements.filter(a => a.announcement !== announcementKey)
      }
   }

   fetchAnnouncements = async () => {
      if (this.shouldFetchAnnouncements) {
         this.announcements = await apiClient.user.getAnnouncements.query()
      }
   }

   subscribeToAnnouncements = async (params: { orgId: string, userId: string }) => {
      if (this.shouldFetchAnnouncements) {
         const { orgId, userId } = params
         await this.sp.webSocketStore.addSubscriptions(
            {
               orgId,
               userId,
               subscriptions: [
                  {
                     event: 'announcement',
                     entityId: orgId,
                     callback: async () => {
                        this.announcements = await apiClient.user.getAnnouncements.query()
                     }
                  }
               ]
            }
         )
      }
   }
}

export const { Provider: AnnouncementStoreProvider, useProvidedStore: useAnnouncementStore } = createStoreContext(AnnouncementStore)
